import React, { useState } from 'react';

const CalculationResult = ({ result, homePrice, homePriceIncrease, years, showHouseAnalysis, averageInterestRate, mortgageAmount }) => {
    const [isBreakdownOpen, setIsBreakdownOpen] = useState(true);

    const formatLargeNumber = (number) => {
        if (number === undefined || number === null) return '0';
        return number.toLocaleString('en-US').replace(/,/g, ' ');
    };

    const canAffordHome = result.years !== -1;

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Results</h2>
            
            {/* Investment results - always show */}
            <div className="stat bg-gray-100 dark:bg-gray-800/50 rounded-xl p-6 md:col-span-2 border-2 border-cyan-400/20 mb-6">
                <div className="stat-title text-gray-600 dark:text-gray-300 text-lg">Total Investment Value</div>
                <div className="stat-value text-3xl md:text-4xl text-cyan-500 dark:text-cyan-400">
                    {formatLargeNumber(result.totalInvestmentValue)}
                </div>
            </div>

            {/* Breakdown Section */}
            <div className="md:col-span-2 bg-gray-100/50 dark:bg-gray-800/30 rounded-xl p-4">
                <button 
                    onClick={() => setIsBreakdownOpen(!isBreakdownOpen)}
                    className="flex items-center justify-between w-full text-gray-600 dark:text-gray-300 text-lg mb-2 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors"
                >
                    <span>Breakdown</span>
                    <svg 
                        className={`w-6 h-6 transform transition-transform duration-300 ${isBreakdownOpen ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                
                <div 
                    className={`space-y-4 overflow-hidden transition-all duration-300 ease-in-out`}
                    style={{ 
                        maxHeight: isBreakdownOpen ? '1000px' : '0',
                        opacity: isBreakdownOpen ? 1 : 0,
                    }}
                >
                    {/* First row with components */}
                    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2 items-center">
                        {/* Initial Investment */}
                        <div className="sm:col-span-2 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border-l-4 border-pink-400">
                            <div className="text-gray-500 dark:text-gray-400 text-sm">Initial Investment</div>
                            <div className="text-lg text-pink-500 dark:text-pink-400">
                                {formatLargeNumber(result.initialInvestment)}
                            </div>
                        </div>

                        {/* Total Contributions */}
                        <div className="sm:col-span-2 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border-l-4 border-purple-400">
                            <div className="text-gray-500 dark:text-gray-400 text-sm">Total Contributions</div>
                            <div className="text-lg text-purple-500 dark:text-purple-400">
                                {formatLargeNumber(result.totalContribution)}
                            </div>
                        </div>

                        {/* Interest Earned */}
                        <div className="sm:col-span-2 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border-l-4 border-emerald-400">
                            <div className="text-gray-500 dark:text-gray-400 text-sm">Interest Earned</div>
                            <div className="text-lg text-emerald-500 dark:text-emerald-400">
                                {formatLargeNumber(result.interestEarned)}
                            </div>
                        </div>
                    </div>

                    {/* Total Amount in new row */}
                    <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border-l-4 border-cyan-400">
                            <div className="text-gray-500 dark:text-gray-300 text-sm">Total Amount</div>
                            <div className="text-lg text-cyan-500 dark:text-cyan-400 font-bold">
                                {formatLargeNumber(result.totalInvestmentValue)}
                            </div>
                        </div>
                    </div>

                    {/* Average Interest Rate in separate row */}
                    <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border-l-4 border-yellow-400">
                        <div>
                            <div className="text-gray-500 dark:text-gray-400">Portfolio Average Interest Rate</div>
                            <div className="text-xl text-yellow-500 dark:text-yellow-400">
                                {averageInterestRate.toFixed(2)}%
                            </div>
                        </div>
                    </div>

                    {/* Mortgage information */}
                    {mortgageAmount > 0 && result.mortgageAtTheEndOfPeriod !== undefined && (
                        <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg border-l-4 border-red-400">
                            <div>
                                <div className="text-gray-500 dark:text-gray-400">Mortgage at End of Period</div>
                                <div className="text-xl text-red-500 dark:text-red-400">
                                    {formatLargeNumber(result.mortgageAtTheEndOfPeriod)}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-500">
                                    Initial mortgage: {formatLargeNumber(mortgageAmount)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* House analysis - only show when showHouseAnalysis is true */}
            {showHouseAnalysis && homePrice > 0 && (
                <div className="stat bg-gray-100 dark:bg-gray-800/50 rounded-xl p-4 md:col-span-2 mt-6">
                    <div className="stat-title text-gray-600 dark:text-gray-400">Savings Goal Analysis</div>
                    <div className="stat-value text-yellow-500 dark:text-yellow-400 break-words whitespace-normal text-wrap text-base sm:text-2xl">
                        {canAffordHome 
                            ? <span className="text-yellow-500 dark:text-yellow-400">
                                You can reach your savings goal in {result.years} years!
                              </span>
                            : <span className="text-red-500 dark:text-red-400">
                                Unable to reach the target amount with current investment plan
                              </span>
                        }
                    </div>
                    <div className="stat-desc text-lg mt-4">
                        <span className="text-gray-600 dark:text-gray-400">Target amount needed: </span>
                        <span className="text-cyan-500 dark:text-cyan-400">{formatLargeNumber(homePrice)}</span>
                    </div>
                    <div className="stat-desc text-lg">
                        <span className="text-gray-600 dark:text-gray-400">Final target with inflation: </span>
                        <span className="text-emerald-500 dark:text-emerald-400">{formatLargeNumber(result.housePriceAtTheEndOfPeriod)}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalculationResult;
